/**
 * Local-interface filter chips — the closed taxonomy + Row 2 applicability,
 * ported verbatim from `designs/chip-row.jsx` (ADR-0011 / ADR-0012). The chip
 * ids match the M3-T03 backend filter (`app/lib/chip_filter.py`); keep them in
 * sync — they are the contract between this component and `GET /api/v1/hospitals`.
 */
import { type IconName } from './Icon'

export type Row1Chip = {
  id: string
  label: string
  icon: IconName
  tag: 'bed' | 'service'
  schedulable: boolean
}

export type Row2Chip = { id: string; label: string; icon: IconName }

/** The active filter, as carried in the URL (`row1` repeated, single `row2`).
 * The chip *control* was retired (ADR-0036) but this vocabulary is still the
 * contract between deep links (`/?row1=icu`) and `GET /api/v1/hospitals`. */
export interface ChipSelection {
  row1: string[]
  row2: string | null
}

export const ROW1: Row1Chip[] = [
  {
    id: 'emergency',
    label: 'Emergency',
    icon: 'sos',
    tag: 'service',
    schedulable: false,
  },
  { id: 'icu', label: 'ICU beds', icon: 'bed', tag: 'bed', schedulable: false },
  {
    id: 'maternity',
    label: 'Maternity',
    icon: 'bed',
    tag: 'bed',
    schedulable: false,
  },
  {
    id: 'private',
    label: 'Private wards',
    icon: 'bed',
    tag: 'bed',
    schedulable: false,
  },
  {
    id: 'executive',
    label: 'Executive suites',
    icon: 'bed',
    tag: 'bed',
    schedulable: false,
  },
  {
    id: 'drugs',
    label: 'Drugs',
    icon: 'pill',
    tag: 'service',
    schedulable: false,
  },
  {
    id: 'labs',
    label: 'Labs',
    icon: 'flask',
    tag: 'service',
    schedulable: true,
  },
  {
    id: 'imaging',
    label: 'Imaging',
    icon: 'image',
    tag: 'service',
    schedulable: true,
  },
  {
    id: 'surgery',
    label: 'Surgery',
    icon: 'knife',
    tag: 'service',
    schedulable: true,
  },
  {
    id: 'ambulance',
    // M34-T06 (ADR-0063): the id stays — it is the URL/API filter vocabulary
    // (ADR-0036) and the backend still filters on it — but the label no longer
    // says "ambulance" on a customer screen. Customers cannot reach it today
    // (`?row1=ambulance` redirects to /transport, M34-T04); this is the words
    // a loosened redirect would show.
    label: 'Hospital vehicle',
    icon: 'truck',
    tag: 'service',
    schedulable: false,
  },
  {
    id: 'chronic',
    label: 'Chronic care',
    icon: 'heart',
    tag: 'service',
    schedulable: true,
  },
  {
    id: 'specialists',
    label: 'Specialists on-site',
    icon: 'user',
    tag: 'service',
    schedulable: true,
  },
]

/**
 * Presentation-only grouping of the Row 1 chips into three labelled categories
 * (ADR-0024). This changes only how the chips are visually clustered — the
 * closed set, ids, labels, filter semantics, and the ROW2 single-select matrix
 * are unchanged. Every ROW1 id appears in exactly one group; `chips.test.ts`
 * guards that invariant.
 */
export type Row1Group = { id: string; label: string; chipIds: string[] }

export const ROW1_GROUPS: Row1Group[] = [
  {
    id: 'bed-availability',
    label: 'Bed availability',
    chipIds: ['icu', 'maternity', 'private', 'executive'],
  },
  {
    id: 'emergency',
    label: 'Emergency',
    chipIds: ['emergency', 'ambulance', 'surgery', 'specialists'],
  },
  {
    id: 'diagnostics-pharmacy',
    label: 'Diagnostics & pharmacy',
    chipIds: ['drugs', 'labs', 'imaging', 'chronic'],
  },
]

export const ROW2: Row2Chip[] = [
  { id: '24hr', label: '24hr', icon: 'clock' },
  { id: 'night', label: 'Night', icon: 'moon' },
  { id: 'fast', label: 'Fast-track', icon: 'fast' },
]

export type Row2Reason = 'no-row1' | 'bed-only' | 'non-sched' | null

export type Row2State = {
  enabled: Record<string, boolean>
  reason: Record<string, Row2Reason>
}

/** Pure: which Row 2 chips are enabled given the selected Row 1 ids, and why
 * each disabled one is disabled. Identical to the backend's `row2_applicable`. */
export function row2State(selected: string[]): Row2State {
  if (selected.length === 0) {
    return {
      enabled: { '24hr': false, night: false, fast: false },
      reason: { '24hr': 'no-row1', night: 'no-row1', fast: 'no-row1' },
    }
  }
  const items = ROW1.filter((r) => selected.includes(r.id))
  const allBed = items.every((i) => i.tag === 'bed')
  const anySched = items.some((i) => i.schedulable)
  if (allBed) {
    return {
      enabled: { '24hr': false, night: false, fast: false },
      reason: { '24hr': 'bed-only', night: 'bed-only', fast: 'bed-only' },
    }
  }
  return {
    enabled: { '24hr': true, night: true, fast: anySched },
    reason: { '24hr': null, night: null, fast: anySched ? null : 'non-sched' },
  }
}

export const TOOLTIP: Record<string, string> = {
  'no-row1': 'Pick a service to filter by hours or appointment time',
  'bed-only':
    'Bed availability is real-time. Pick a service for time-based filters',
  'non-sched': 'Fast-track applies to appointment-based services',
}
